// src/utils/imageUtils.ts
// Phase 4.3: Image optimization utilities

/**
 * Get optimized image URL with size transformation
 * Uses Cloudinary URL transformations for different sizes
 * 
 * @param imageUrl - Original image URL (Cloudinary or other)
 * @param size - Desired size: 'small' (40x40), 'medium' (50x50), 'large' (100x100), 'xlarge' (200x200)
 * @returns Optimized image URL with size transformation
 */
export const getOptimizedImageUrl = (
  imageUrl: string | null | undefined,
  size: 'small' | 'medium' | 'large' | 'xlarge' = 'medium'
): string => {
  // Return fallback if no image URL
  if (!imageUrl) {
    return '';
  }

  // If not a Cloudinary URL, return as-is (for Pravatar or other providers)
  if (!imageUrl.includes('cloudinary.com')) {
    return imageUrl;
  }

  // Cloudinary size transformations with WebP format (Phase 4.4)
  // f_auto: Automatically selects best format (WebP for modern browsers, fallback to original)
  // q_auto: Automatic quality optimization
  const sizeMap = {
    small: 'w_40,h_40,c_fill,f_auto,q_auto',    // 40x40px for Messages
    medium: 'w_50,h_50,c_fill,f_auto,q_auto',   // 50x50px for Dashboard
    large: 'w_100,h_100,c_fill,f_auto,q_auto',  // 100x100px for Discover
    xlarge: 'w_200,h_200,c_fill,f_auto,q_auto'  // 200x200px for ProfilePage
  };

  const transformation = sizeMap[size];

  // Insert transformation into Cloudinary URL
  // Cloudinary URL format: https://res.cloudinary.com/{cloud_name}/image/upload/{transformation}/{public_id}.{format}
  // We need to insert the transformation after '/upload/'
  if (imageUrl.includes('/upload/')) {
    const parts = imageUrl.split('/upload/');
    if (parts.length === 2) {
      return `${parts[0]}/upload/${transformation}/${parts[1]}`;
    }
  }

  // Fallback: return original URL if transformation fails
  return imageUrl;
};

/**
 * Get profile image URL (no Pravatar fallback).
 * Use Avatar component for initials placeholder when no profile picture.
 *
 * @param profilePicture - User's profile picture URL
 * @param _userId - Unused (kept for API compatibility)
 * @param size - Desired size transformation
 * @returns Profile image URL (optimized) or null when no picture
 */
export const getProfileImageUrl = (
  profilePicture: string | null | undefined,
  _userId?: string,
  size: 'small' | 'medium' | 'large' | 'xlarge' = 'medium'
): string | null => {
  if (profilePicture) {
    return getOptimizedImageUrl(profilePicture, size);
  }
  return null;
};
